// Per-consultation recording state kept in sessionStorage so a reload or in-app remount can
// resume the clock, segment index, pause reason, and any audio segments not yet acknowledged.
const RECORDING_SESSION_KEY_PREFIX = 'scribe-recording-session:';
const PENDING_MIC_DEVICE_KEY = 'scribe-pending-mic-device';

export const defaultDurationWarningsShown = () => ({ warn15: false, warn25: false });

const getRecordingSessionKey = (id) => `${RECORDING_SESSION_KEY_PREFIX}${id}`;

const isQuotaExceededError = (error) => {
  if (!error) return false;
  return (
    error.name === 'QuotaExceededError'
    || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || error.code === 22
    || error.code === 1014
  );
};

export const loadRecordingSession = (id) => {
  try {
    const raw = sessionStorage.getItem(getRecordingSessionKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Persist recording session metadata + pending (not-yet-acked) segments.
 * @returns {{ ok: boolean, quotaExceeded?: boolean }}
 */
export const saveRecordingSession = (id, snapshot) => {
  try {
    sessionStorage.setItem(getRecordingSessionKey(id), JSON.stringify(snapshot));
    return { ok: true };
  } catch (error) {
    if (isQuotaExceededError(error)) {
      // Keep metadata so remount can resume clock/index; pending audio stays in memory only.
      try {
        const { pendingSegments, ...meta } = snapshot || {};
        sessionStorage.setItem(getRecordingSessionKey(id), JSON.stringify({
          ...meta,
          pendingSegments: [],
          storageQuotaExceeded: true
        }));
      } catch {
        // ignore secondary failure
      }
      return { ok: false, quotaExceeded: true };
    }
    return { ok: false };
  }
};

export const clearRecordingSession = (id) => {
  try {
    sessionStorage.removeItem(getRecordingSessionKey(id));
  } catch {
    // ignore
  }
};

export const mergePendingSegments = (existing, incoming) => {
  const byIndex = new Map();
  (existing || []).forEach((segment) => {
    if (segment && typeof segment.index === 'number' && segment.data) {
      byIndex.set(segment.index, segment);
    }
  });
  (incoming || []).forEach((segment) => {
    if (segment && typeof segment.index === 'number' && segment.data) {
      byIndex.set(segment.index, segment);
    }
  });
  return Array.from(byIndex.values()).sort((a, b) => a.index - b.index);
};

export const prunePendingSegment = (segments, index) => (
  (segments || []).filter((segment) => segment.index !== index)
);

/** The microphone chosen when the recording was started elsewhere; read once, then cleared. */
export const readPendingMicDevice = () => {
  try {
    const deviceId = sessionStorage.getItem(PENDING_MIC_DEVICE_KEY);
    if (deviceId) sessionStorage.removeItem(PENDING_MIC_DEVICE_KEY);
    return deviceId;
  } catch {
    return null;
  }
};
